<template>
  <div class="diag-root">
    <div class="diag-page">
      <header class="diag-brand" role="banner" aria-label="e2m.live — the event software co.">
        <div class="diag-brand-wordmark">e2m<span class="diag-dot">.</span>live</div>
        <div class="diag-brand-tagline">the event software co.</div>
      </header>

      <div class="diag-card">
        <div class="diag-card-header">
          <h1>Event Platform — Network Diagnostic</h1>
          <p>
            Run this from inside the network you will use during the live event.
            Share the result (Copy report) with your IT team if any check fails.
          </p>
        </div>

        <div class="diag-controls">
          <button id="diag-runAll">Re-run all checks</button>
          <button id="diag-askMedia" class="secondary">Test camera &amp; microphone</button>
          <button id="diag-copy" class="secondary">Copy report</button>
          <button id="diag-download" class="secondary">Download report</button>
        </div>

        <section class="diag-panel">
          <h2>Your connection</h2>
          <p class="diag-panel-desc">Public IP, location, and whether a VPN or proxy is in the path.</p>
          <div id="diag-ip-summary" class="diag-kv-grid">
            <div class="k">Status</div>
            <div>Detecting&hellip;</div>
          </div>
        </section>

        <section class="diag-panel">
          <h2>Platform service reachability</h2>
          <p class="diag-panel-desc">
            Each row makes a request from your browser and reports whether the
            domain responded. <code>opaque OK</code> means the response was reachable
            but the body is hidden by CORS (normal for cross-origin GETs) — the
            connection itself works.
          </p>
          <table class="diag-table">
            <thead>
              <tr>
                <th style="width: 55%">Service</th>
                <th class="diag-timing-col">Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody id="diag-services-tbody"></tbody>
          </table>
        </section>

        <section class="diag-panel">
          <h2>WebRTC capability</h2>
          <p class="diag-panel-desc">
            Tests whether your browser can establish a WebRTC connection — required for
            live audio/video.
          </p>
          <div id="diag-webrtc-results" class="diag-kv-grid">
            <div class="k">Status</div>
            <div>Pending&hellip;</div>
          </div>
        </section>

        <section class="diag-panel">
          <h2>Camera &amp; microphone</h2>
          <p class="diag-panel-desc">
            Click the &ldquo;Test camera &amp; microphone&rdquo; button above to prompt for permission.
            This will not auto-run.
          </p>
          <div id="diag-media-results" class="diag-kv-grid">
            <div class="k">Status</div>
            <div>Not yet tested</div>
          </div>
        </section>

        <section class="diag-panel">
          <h2>Browser &amp; network info</h2>
          <div id="diag-browser-info" class="diag-kv-grid"></div>
        </section>
      </div>

      <p class="diag-footer">e2m.live — share this URL with your IT team to diagnose access issues.</p>
    </div>
  </div>
</template>

<script>
import jQuery from "jquery";
const $ = jQuery;

export default {
  name: "EventDiagnostic",
  mounted() {
    // Hide the app's top/left navigation so the page renders full-screen.
    $("#topmenu").hide();
    $("#leftmenu").hide();
    this.startDiagnostic();
  },
  beforeDestroy() {
    // Restore app chrome when leaving the page.
    $("#topmenu").show();
    $("#leftmenu").show();
  },
  methods: {
    startDiagnostic() {
      // ───────── config ─────────
      const VPNAPI_KEY = "1ce02971c07e4c1c976909614a8f9a20";

      const PLATFORM_SERVICES = [
        { group: "Platform", label: "e2m.live (root)",                url: "https://e2m.live/favicon.ico" },
        { group: "Platform", label: "Production app (onair)",         url: "https://onair.e2m.live/favicon.ico" },
        { group: "Platform", label: "Branding/resources",             url: "https://resources.e2m.live/favicon.ico" },
        { group: "Platform", label: "AWS API Gateway",                url: "https://8jjlnlhca1.execute-api.us-east-1.amazonaws.com/" },
        { group: "Firebase", label: "Cloud Functions (prod)",         url: "https://us-central1-e2monair.cloudfunctions.net/" },
        { group: "Firebase", label: "Firebase Storage",               url: "https://firebasestorage.googleapis.com/" },
        { group: "Firebase", label: "Firebase Auth domain",           url: "https://e2monair.firebaseapp.com/" },
        { group: "Firebase", label: "Google APIs",                    url: "https://www.googleapis.com/" },
        { group: "Firebase", label: "Google Sign-in",                 url: "https://accounts.google.com/" },
        { group: "Firebase", label: "Google static (gstatic)",        url: "https://www.gstatic.com/" },
        { group: "Firebase", label: "FCM (push)",                     url: "https://fcm.googleapis.com/" },
        { group: "Firebase", label: "reCAPTCHA",                      url: "https://www.google.com/recaptcha/api.js" },
        { group: "Agora",    label: "Agora SD-RTN",                   url: "https://web-2.ap.sd-rtn.com/" },
        { group: "CDN",      label: "jsDelivr",                       url: "https://cdn.jsdelivr.net/" },
        { group: "CDN",      label: "cdnjs (Cloudflare)",             url: "https://cdnjs.cloudflare.com/" },
        { group: "CDN",      label: "Google Fonts (CSS)",             url: "https://fonts.googleapis.com/" },
        { group: "CDN",      label: "Google Fonts (static)",          url: "https://fonts.gstatic.com/" },
        { group: "CDN",      label: "ajax.googleapis (jQuery)",       url: "https://ajax.googleapis.com/" },
      ];

      const FIREBASE_RTDB_PROJECTS = [
        { label: "RTDB prod (e2monair)",    ns: "e2monair" },
      ];

      const STUN_SERVERS = ["stun:stun.l.google.com:19302", "stun:stun.agora.io:3478"];

      // ───────── state / report ─────────
      const report = {
        generatedAt: new Date().toISOString(),
        ip: null,
        ipInfo: null,
        browser: {},
        services: [],
        firebaseRTDB: [],
        webrtc: {},
        media: { camera: "not-tested", microphone: "not-tested" },
      };

      // ───────── DOM refs ─────────
      const $ip       = document.getElementById("diag-ip-summary");
      const $services = document.getElementById("diag-services-tbody");
      const $webrtc   = document.getElementById("diag-webrtc-results");
      const $media    = document.getElementById("diag-media-results");
      const $browser  = document.getElementById("diag-browser-info");
      const $runAll   = document.getElementById("diag-runAll");
      const $askMedia = document.getElementById("diag-askMedia");
      const $copy     = document.getElementById("diag-copy");
      const $download = document.getElementById("diag-download");

      // ───────── helpers ─────────
      function esc(s) {
        if (s === null || s === undefined) return "";
        return String(s)
          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      }
      function badge(level, text) {
        return '<span class="diag-badge ' + level + '">' + esc(text) + "</span>";
      }
      function kv($el, rows) {
        $el.innerHTML = rows.map(function (r) {
          return '<div class="k">' + esc(r[0]) + "</div><div>" + r[1] + "</div>";
        }).join("");
      }
      function withTimeout(promise, ms) {
        return Promise.race([
          promise,
          new Promise(function (_, rej) {
            setTimeout(function () { rej(new Error("timeout")); }, ms);
          }),
        ]);
      }
      function fetchProbe(url, ms) {
        const ctrl = (typeof AbortController !== "undefined") ? new AbortController() : null;
        const opts = { method: "GET", mode: "no-cors", cache: "no-store" };
        if (ctrl) opts.signal = ctrl.signal;
        const t = setTimeout(function () { if (ctrl) ctrl.abort(); }, ms);
        const t0 = performance.now();
        return fetch(url, opts).then(function (res) {
          clearTimeout(t);
          const dt = Math.round(performance.now() - t0);
          return { ok: true, ms: dt, status: res.status, type: res.type };
        }).catch(function (err) {
          clearTimeout(t);
          const dt = Math.round(performance.now() - t0);
          return { ok: false, ms: dt, error: (err && err.message) || String(err) };
        });
      }
      function rememberIdleLabel($btn) {
        if (!$btn.dataset.idleLabel) $btn.dataset.idleLabel = $btn.textContent;
      }
      function setBusyButton($btn, busyLabel) {
        rememberIdleLabel($btn);
        if ($btn._diagResetTimer) {
          clearTimeout($btn._diagResetTimer);
          $btn._diagResetTimer = null;
        }
        $btn.disabled = true;
        $btn.textContent = busyLabel;
        $btn.classList.add("diag-busy");
        $btn.classList.remove("diag-complete");
        $btn.setAttribute("aria-busy", "true");
      }
      function completeButton($btn, doneLabel) {
        rememberIdleLabel($btn);
        $btn.disabled = true;
        $btn.textContent = doneLabel;
        $btn.classList.remove("diag-busy");
        $btn.classList.add("diag-complete");
        $btn.setAttribute("aria-busy", "false");
        $btn._diagResetTimer = setTimeout(function () {
          $btn.disabled = false;
          $btn.textContent = $btn.dataset.idleLabel;
          $btn.classList.remove("diag-busy", "diag-complete");
          $btn.removeAttribute("aria-busy");
          $btn._diagResetTimer = null;
        }, 1200);
      }

      // ───────── IP + intel ─────────
      async function checkIP() {
        kv($ip, [["Status", '<span class="diag-badge pending">running</span> Detecting…']]);
        try {
          const r = await withTimeout(fetch("https://api.ipify.org?format=json"), 5000);
          const d = await r.json();
          report.ip = d.ip;
        } catch (_) { report.ip = null; }

        if (report.ip) {
          try {
            const r2 = await withTimeout(
              fetch("https://vpnapi.io/api/" + encodeURIComponent(report.ip) + "?key=" + VPNAPI_KEY),
              6000
            );
            report.ipInfo = await r2.json();
          } catch (_) { report.ipInfo = null; }
        }
        renderIP();
      }
      function renderIP() {
        const rows = [];
        if (!report.ip) {
          rows.push(["IP address", badge("error", "unknown") + " could not be detected — likely blocked by ad-blocker or firewall"]);
          kv($ip, rows); return;
        }
        rows.push(["IP address", "<code>" + esc(report.ip) + "</code>"]);
        const info = report.ipInfo;
        if (info && info.location) {
          rows.push(["Location",
            esc((info.location.city ? info.location.city + ", " : "") +
                (info.location.region ? info.location.region + ", " : "") +
                (info.location.country || ""))]);
        }
        if (info && info.network && info.network.autonomous_system_organization) {
          rows.push(["ISP / Organisation", esc(info.network.autonomous_system_organization)]);
        }
        if (info && info.security) {
          const s = info.security;
          const flags = [];
          if (s.vpn)   flags.push(badge("warning", "VPN"));
          if (s.proxy) flags.push(badge("warning", "Proxy"));
          if (s.tor)   flags.push(badge("error", "Tor"));
          if (s.relay) flags.push(badge("info", "Private Relay"));
          rows.push(["Security flags", flags.length ? flags.join(" ") : badge("success", "clean")]);
        }
        kv($ip, rows);
      }

      // ───────── Platform service probes ─────────
      function renderServices() {
        $services.innerHTML = report.services.map(function (s) {
          let b;
          if (s.state === "pending")      b = badge("pending", "running");
          else if (s.state === "ok")      b = badge("success", "reachable");
          else if (s.state === "opaque")  b = badge("warning", "opaque OK");
          else if (s.state === "fail")    b = badge("error", "blocked");
          return '<tr>' +
                   '<td><strong>' + esc(s.label) + '</strong><br><span style="color:var(--diag-muted);font-size:12px">' + esc(s.url) + '</span></td>' +
                   '<td class="diag-timing-col">' + (s.ms != null ? esc(s.ms + " ms") : "—") + '</td>' +
                   '<td class="diag-status">' + b + (s.error ? '<div style="color:var(--diag-error);font-size:12px;margin-top:4px">' + esc(s.error) + '</div>' : '') + '</td>' +
                 '</tr>';
        }).join("");
      }
      async function probeServices() {
        report.services = PLATFORM_SERVICES.map(function (s) {
          return { label: s.label, url: s.url, group: s.group, state: "pending", ms: null };
        });
        renderServices();
        for (let i = 0; i < report.services.length; i++) {
          const s = report.services[i];
          const r = await fetchProbe(s.url, 6000);
          if (r.ok) {
            s.state = (r.type === "opaque") ? "opaque" : "ok";
          } else {
            s.state = "fail";
            s.error = r.error;
          }
          s.ms = r.ms;
          renderServices();
        }
      }

      // ───────── Firebase Realtime DB WSS probe ─────────
      async function probeRTDB() {
        for (let i = 0; i < FIREBASE_RTDB_PROJECTS.length; i++) {
          const p = FIREBASE_RTDB_PROJECTS[i];
          const url = "wss://" + p.ns + ".firebaseio.com/.ws?v=5&ns=" + p.ns;
          const out = { label: p.label, url: url, state: "pending", ms: null };
          report.firebaseRTDB.push(out);
          $services.insertAdjacentHTML("beforeend",
            '<tr data-rtdb="' + esc(p.ns) + '">' +
              '<td><strong>' + esc(p.label) + ' (WebSocket)</strong><br><span style="color:var(--diag-muted);font-size:12px">' + esc(url) + '</span></td>' +
              '<td class="diag-timing-col">—</td>' +
              '<td class="diag-status">' + badge("pending", "running") + '</td>' +
            '</tr>'
          );
        }
        for (let j = 0; j < report.firebaseRTDB.length; j++) {
          await probeOneRTDB(report.firebaseRTDB[j]);
        }
      }
      function probeOneRTDB(entry) {
        return new Promise(function (resolve) {
          const t0 = performance.now();
          let ws;
          let settled = false;
          const timer = setTimeout(function () {
            if (settled) return; settled = true;
            entry.state = "fail"; entry.error = "WebSocket timeout";
            try { ws && ws.close(); } catch (_) { /* ignore */ }
            updateRTDBRow(entry); resolve();
          }, 7000);
          try {
            ws = new WebSocket(entry.url);
            ws.onopen = function () {
              if (settled) return; settled = true;
              clearTimeout(timer);
              entry.state = "ok";
              entry.ms = Math.round(performance.now() - t0);
              try { ws.close(); } catch (_) { /* ignore */ }
              updateRTDBRow(entry); resolve();
            };
            ws.onerror = function () {
              if (settled) return; settled = true;
              clearTimeout(timer);
              entry.state = "fail"; entry.error = "WebSocket error";
              entry.ms = Math.round(performance.now() - t0);
              updateRTDBRow(entry); resolve();
            };
          } catch (e) {
            settled = true; clearTimeout(timer);
            entry.state = "fail"; entry.error = e.message;
            updateRTDBRow(entry); resolve();
          }
        });
      }
      function updateRTDBRow(entry) {
        const ns = entry.url.match(/wss:\/\/([^.]+)\./)[1];
        const $row = document.querySelector('tr[data-rtdb="' + ns + '"]');
        if (!$row) return;
        const b = entry.state === "ok" ? badge("success", "reachable") : badge("error", "blocked");
        $row.children[1].textContent = entry.ms != null ? entry.ms + " ms" : "—";
        $row.children[2].innerHTML = b + (entry.error ? '<div style="color:var(--diag-error);font-size:12px;margin-top:4px">' + esc(entry.error) + '</div>' : '');
      }

      // ───────── WebRTC capability ─────────
      async function probeWebRTC() {
        kv($webrtc, [["Status", badge("pending", "running")]]);
        if (!window.RTCPeerConnection) {
          report.webrtc = { supported: false };
          kv($webrtc, [["WebRTC supported", badge("error", "no") + " — your browser does not support WebRTC"]]);
          return;
        }
        const pc = new RTCPeerConnection({ iceServers: STUN_SERVERS.map(function (u) { return { urls: u }; }) });
        const candidates = [];
        pc.onicecandidate = function (e) { if (e.candidate) candidates.push(e.candidate); };

        pc.createDataChannel("probe");
        try {
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
        } catch (e) {
          report.webrtc = { supported: true, error: e.message };
          kv($webrtc, [["WebRTC supported", badge("error", "error") + " " + esc(e.message)]]);
          try { pc.close(); } catch (_) { /* ignore */ }
          return;
        }

        await new Promise(function (resolve) {
          let done = false;
          pc.onicegatheringstatechange = function () {
            if (pc.iceGatheringState === "complete" && !done) { done = true; resolve(); }
          };
          setTimeout(function () { if (!done) { done = true; resolve(); } }, 6000);
        });

        const hasHost  = candidates.some(function (c) { return c.type === "host"; });
        const hasSrflx = candidates.some(function (c) { return c.type === "srflx"; });
        const hasRelay = candidates.some(function (c) { return c.type === "relay"; });
        const publicIPs = candidates.filter(function (c) { return c.type === "srflx"; })
          .map(function (c) { return c.address || c.ip; });

        report.webrtc = {
          supported: true,
          candidateTypes: { host: hasHost, srflx: hasSrflx, relay: hasRelay },
          srflxAddresses: publicIPs,
        };

        try { pc.close(); } catch (_) { /* ignore */ }

        const rows = [];
        rows.push(["WebRTC supported", badge("success", "yes")]);
        rows.push(["Local network (host)", hasHost ? badge("success", "ok") : badge("warning", "none")]);
        rows.push(["Public NAT (srflx)",   hasSrflx ? badge("success", "ok") + " — STUN reachable, NAT traversal works" : badge("error", "fail") + " — STUN unreachable or UDP blocked"]);
        rows.push(["Relay (TURN)",         hasRelay ? badge("success", "ok") : badge("info", "not tested") + " — relay servers were not configured for this probe"]);
        if (publicIPs.length) rows.push(["Detected public IP(s)", esc(publicIPs.join(", "))]);
        if (!hasSrflx) {
          rows.push(["", '<div class="diag-alert error">UDP / STUN appears blocked on this network. Live audio &amp; video will not connect — please ask IT to allow UDP 3478 outbound and the <code>*.agora.io</code>, <code>stun.l.google.com</code> hosts.</div>']);
        }
        kv($webrtc, rows);
      }

      // ───────── Camera / mic ─────────
      async function testMedia() {
        setBusyButton($askMedia, "Testing...");
        try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          kv($media, [["Status", badge("error", "unsupported") + " getUserMedia not available"]]);
          return;
        }
        kv($media, [["Status", badge("pending", "requesting") + " — please allow the browser prompt"]]);
        const rows = [];
        let stream = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
          const tracks = stream.getTracks();
          report.media.camera = "granted";
          report.media.microphone = "granted";
          rows.push(["Camera",     badge("success", "granted")]);
          rows.push(["Microphone", badge("success", "granted")]);
          const devices = [];
          tracks.forEach(function (t) { devices.push(t.kind + ": " + (t.label || "(unnamed)")); });
          rows.push(["Detected devices", esc(devices.join(" / "))]);
        } catch (e) {
          const msg = (e && e.name) || "error";
          if (msg === "NotAllowedError") {
            report.media.camera = "denied"; report.media.microphone = "denied";
            rows.push(["Status", badge("error", "denied") + " — permission was refused by the user or by browser policy"]);
          } else if (msg === "NotFoundError") {
            rows.push(["Status", badge("error", "no device") + " — no camera/microphone attached"]);
          } else {
            rows.push(["Status", badge("error", msg) + " " + esc((e && e.message) || "")]);
          }
        } finally {
          if (stream) stream.getTracks().forEach(function (t) { t.stop(); });
        }
        kv($media, rows);
        } finally {
          completeButton($askMedia, "Completed");
        }
      }

      // ───────── Browser / network info ─────────
      function renderBrowser() {
        const ua = navigator.userAgent;
        const rows = [
          ["User agent", "<code style='font-size:11px'>" + esc(ua) + "</code>"],
          ["Platform", esc(navigator.platform || "—")],
          ["Language", esc(navigator.language || "—")],
          ["Online", navigator.onLine ? badge("success", "yes") : badge("error", "no")],
          ["Cookies enabled", navigator.cookieEnabled ? badge("success", "yes") : badge("warning", "no")],
          ["Time zone", esc(Intl.DateTimeFormat().resolvedOptions().timeZone || "—")],
          ["Local time", esc(new Date().toString())],
        ];
        const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (c) {
          if (c.effectiveType) rows.push(["Connection class", esc(c.effectiveType)]);
          if (c.downlink != null) rows.push(["Downlink (Mbps)", esc(String(c.downlink))]);
          if (c.rtt != null) rows.push(["RTT (ms)", esc(String(c.rtt))]);
          if (c.type) rows.push(["Connection type", esc(c.type)]);
        }
        report.browser = {
          userAgent: ua, platform: navigator.platform, language: navigator.language,
          online: navigator.onLine, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          connection: c ? {
            effectiveType: c.effectiveType, downlink: c.downlink, rtt: c.rtt, type: c.type
          } : null,
        };
        kv($browser, rows);
      }

      // ───────── orchestrator ─────────
      async function runAll() {
        setBusyButton($runAll, "Running...");
        try {
          report.generatedAt = new Date().toISOString();
          report.services = []; report.firebaseRTDB = [];
          renderBrowser();
          await Promise.all([checkIP(), probeServices()]);
          await probeRTDB();
          await probeWebRTC();
        } finally {
          completeButton($runAll, "Completed");
        }
      }

      // ───────── report copy / download ─────────
      function buildReportText() { return JSON.stringify(report, null, 2); }
      $copy.addEventListener("click", function () {
        const txt = buildReportText();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(txt).then(function () {
            $copy.textContent = "Copied ✓";
            setTimeout(function () { $copy.textContent = "Copy report"; }, 2000);
          });
        } else {
          const ta = document.createElement("textarea");
          ta.value = txt; document.body.appendChild(ta);
          ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
          $copy.textContent = "Copied ✓";
          setTimeout(function () { $copy.textContent = "Copy report"; }, 2000);
        }
      });
      $download.addEventListener("click", function () {
        const blob = new Blob([buildReportText()], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "e2m-network-diagnostic-" + Date.now() + ".json";
        a.click();
        setTimeout(function () { URL.revokeObjectURL(url); }, 0);
      });
      $runAll.addEventListener("click", runAll);
      $askMedia.addEventListener("click", testMedia);

      runAll();
    },
  },
};
</script>

<style>
/*
 * All rules are prefixed with .diag-root so they don't leak.
 * Inner rules use a doubled .diag-root.diag-root prefix to bump specificity
 * above Vuetify's .theme--light / .v-application class selectors which would
 * otherwise repaint the diagnostic with the host app's colors and typography.
 */

.diag-root.diag-root {
  --diag-bg: #f4f6fb;
  --diag-card: #ffffff;
  --diag-text: #1f2937;
  --diag-muted: #6b7280;
  --diag-border: #e5e7eb;
  --diag-primary: #1976d2;
  --diag-success: #2e7d32;
  --diag-warning: #ed6c02;
  --diag-error: #c62828;
  --diag-info: #0277bd;
  --diag-pending: #6b7280;

  position: fixed;
  inset: 0;
  overflow: auto;
  background: var(--diag-bg);
  color: var(--diag-text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.45;
  letter-spacing: normal;
  text-transform: none;
  text-align: left;
  z-index: 2000;
}

/* Reset every descendant against Vuetify / global styles. */
.diag-root.diag-root *,
.diag-root.diag-root *::before,
.diag-root.diag-root *::after {
  box-sizing: border-box;
  font-family: inherit;
  font-weight: inherit;
  font-style: inherit;
  letter-spacing: normal;
  text-transform: none;
  text-shadow: none;
  text-decoration: none;
}
.diag-root.diag-root h1,
.diag-root.diag-root h2,
.diag-root.diag-root h3,
.diag-root.diag-root h4,
.diag-root.diag-root p,
.diag-root.diag-root ul,
.diag-root.diag-root ol,
.diag-root.diag-root li,
.diag-root.diag-root table,
.diag-root.diag-root th,
.diag-root.diag-root td,
.diag-root.diag-root section,
.diag-root.diag-root div,
.diag-root.diag-root span,
.diag-root.diag-root strong,
.diag-root.diag-root header {
  background: transparent;
  color: inherit;
  margin: 0;
  padding: 0;
  border: 0;
}
.diag-root.diag-root strong { font-weight: 600; }
.diag-root.diag-root a { color: var(--diag-primary); text-decoration: none; }

.diag-root.diag-root .diag-page {
  max-width: 880px;
  margin: 28px auto;
  padding: 0 16px;
}

.diag-root.diag-root .diag-brand {
  text-align: center;
  margin: 0 0 18px;
}
.diag-root.diag-root .diag-brand-wordmark {
  font-weight: 900;
  font-size: 44px;
  line-height: 1;
  letter-spacing: -1.5px;
  color: #111;
}
.diag-root.diag-root .diag-brand-wordmark .diag-dot { color: #e53935; }
.diag-root.diag-root .diag-brand-tagline {
  margin-top: 6px;
  font-size: 13px;
  letter-spacing: 0.4px;
  color: #111;
}
@media (max-width: 480px) {
  .diag-root.diag-root .diag-brand-wordmark { font-size: 36px; }
}

.diag-root.diag-root .diag-card {
  background: var(--diag-card);
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.diag-root.diag-root .diag-card-header {
  padding: 18px 22px 14px;
  border-bottom: 1px solid var(--diag-border);
}
.diag-root.diag-root .diag-card-header h1 {
  font-size: 20px;
  font-weight: 600;
  color: var(--diag-text);
}
.diag-root.diag-root .diag-card-header p {
  margin: 6px 0 0;
  color: var(--diag-muted);
  font-size: 14px;
}

.diag-root.diag-root .diag-controls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 14px 22px;
  border-bottom: 1px solid var(--diag-border);
  background: #fafafa;
}
/* Buttons: !important on the visual props so global button rules
   (Bootstrap, Vuetify, app-level .btn / button overrides) cannot repaint them. */
.diag-root.diag-root .diag-controls button,
.diag-root.diag-root .diag-controls .diag-control-link {
  -webkit-appearance: none !important;
  appearance: none !important;
  background: var(--diag-primary) !important;
  background-image: none !important;
  color: #fff !important;
  border: 0 !important;
  border-radius: 6px !important;
  padding: 8px 14px !important;
  margin: 0 !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  font-family: inherit !important;
  line-height: 1.45 !important;
  letter-spacing: normal !important;
  text-transform: none !important;
  text-shadow: none !important;
  box-shadow: none !important;
  cursor: pointer;
  height: auto !important;
  min-width: 0 !important;
  min-height: 0 !important;
  outline: none;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
  position: relative;
  overflow: hidden;
}
.diag-root.diag-root .diag-controls button.diag-busy {
  color: #ffffff !important;
}
.diag-root.diag-root .diag-controls button.diag-busy::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 20%, rgba(255, 255, 255, 0.35) 50%, transparent 80%);
  transform: translateX(-140%);
  animation: diag-button-shimmer 1.1s linear infinite;
}
.diag-root.diag-root .diag-controls button.diag-busy.secondary {
  color: var(--diag-primary) !important;
}
.diag-root.diag-root .diag-controls button.diag-busy.secondary::after {
  background: linear-gradient(110deg, transparent 20%, rgba(25, 118, 210, 0.12) 50%, transparent 80%);
}
.diag-root.diag-root .diag-controls button.diag-complete {
  background: var(--diag-success) !important;
  background-image: none !important;
  color: #ffffff !important;
  border-color: var(--diag-success) !important;
}
.diag-root.diag-root .diag-controls button:hover,
.diag-root.diag-root .diag-controls .diag-control-link:hover {
  background: #155fa0 !important;
  background-image: none !important;
  color: #fff !important;
}
.diag-root.diag-root .diag-controls button:focus,
.diag-root.diag-root .diag-controls button:focus-visible,
.diag-root.diag-root .diag-controls .diag-control-link:focus,
.diag-root.diag-root .diag-controls .diag-control-link:focus-visible {
  outline: 2px solid rgba(25, 118, 210, 0.4);
  outline-offset: 1px;
}
.diag-root.diag-root .diag-controls button.secondary,
.diag-root.diag-root .diag-controls .diag-control-link.secondary {
  background: #ffffff !important;
  background-image: none !important;
  color: var(--diag-primary) !important;
  border: 1px solid var(--diag-primary) !important;
}
.diag-root.diag-root .diag-controls button.secondary:hover,
.diag-root.diag-root .diag-controls .diag-control-link.secondary:hover {
  background: var(--diag-primary) !important;
  background-image: none !important;
  color: #ffffff !important;
}
.diag-root.diag-root .diag-controls button.diag-complete.secondary {
  background: var(--diag-success) !important;
  color: #ffffff !important;
  border-color: var(--diag-success) !important;
}
.diag-root.diag-root .diag-controls button:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed;
}
.diag-root.diag-root .diag-controls button.diag-busy:disabled,
.diag-root.diag-root .diag-controls button.diag-complete:disabled {
  opacity: 1 !important;
}
.diag-root.diag-root .diag-controls button.diag-busy:disabled {
  cursor: progress;
}
@keyframes diag-button-shimmer {
  from { transform: translateX(-140%); }
  to { transform: translateX(140%); }
}
.diag-root.diag-root .diag-panel {
  padding: 16px 22px;
  border-bottom: 1px solid var(--diag-border);
}
.diag-root.diag-root .diag-panel:last-child { border-bottom: 0; }
.diag-root.diag-root .diag-panel h2 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--diag-text);
}
.diag-root.diag-root .diag-panel-desc {
  color: var(--diag-muted);
  font-size: 13px;
  margin: 0 0 12px;
}

.diag-root.diag-root .diag-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: var(--diag-text);
  background: transparent;
}
.diag-root.diag-root .diag-table th,
.diag-root.diag-root .diag-table td {
  text-align: left;
  padding: 6px 8px;
  vertical-align: top;
  background: transparent;
  color: var(--diag-text);
  font-weight: 400;
}
.diag-root.diag-root .diag-table th {
  color: var(--diag-muted);
  font-weight: 500;
  border-bottom: 1px solid var(--diag-border);
}
.diag-root.diag-root .diag-table td.diag-status { white-space: nowrap; }

/* Badges and alerts — force colors with !important so they can't be repainted
   by any host stylesheet that targets generic .success / .error class names. */
.diag-root.diag-root .diag-badge {
  display: inline-block;
  min-width: 60px;
  text-align: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
.diag-root.diag-root .diag-badge.success {
  background: rgba(46, 125, 50, 0.15) !important;
  color: var(--diag-success) !important;
}
.diag-root.diag-root .diag-badge.warning {
  background: rgba(237, 108, 2, 0.15) !important;
  color: var(--diag-warning) !important;
}
.diag-root.diag-root .diag-badge.error {
  background: rgba(198, 40, 40, 0.15) !important;
  color: var(--diag-error) !important;
}
.diag-root.diag-root .diag-badge.info {
  background: rgba(2, 119, 189, 0.15) !important;
  color: var(--diag-info) !important;
}
.diag-root.diag-root .diag-badge.pending {
  background: rgba(107, 114, 128, 0.15) !important;
  color: var(--diag-pending) !important;
}

.diag-root.diag-root .diag-kv-grid {
  display: grid;
  grid-template-columns: 170px 1fr;
  gap: 4px 14px;
  font-size: 13px;
  color: var(--diag-text);
}
.diag-root.diag-root .diag-kv-grid > .k { color: var(--diag-muted); }

.diag-root.diag-root .diag-alert {
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  font-size: 13px;
}
.diag-root.diag-root .diag-alert.warning {
  background: rgba(237, 108, 2, 0.10) !important;
  color: var(--diag-warning) !important;
}
.diag-root.diag-root .diag-alert.error {
  background: rgba(198, 40, 40, 0.10) !important;
  color: var(--diag-error) !important;
}
.diag-root.diag-root .diag-alert.success {
  background: rgba(46, 125, 50, 0.10) !important;
  color: var(--diag-success) !important;
}

.diag-root.diag-root code {
  background: #eef1f6;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: var(--diag-text);
}

.diag-root.diag-root .diag-footer {
  text-align: center;
  color: var(--diag-muted);
  font-size: 12px;
  margin-top: 14px;
}

@media (max-width: 600px) {
  .diag-root.diag-root .diag-kv-grid { grid-template-columns: 1fr; }
  .diag-root.diag-root .diag-table th.diag-timing-col,
  .diag-root.diag-root .diag-table td.diag-timing-col { display: none; }
}
</style>
